import { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useMaterialUIController, setSelection } from "context";
import { useLocation } from "react-router-dom";
import { useMediaQuery, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { position } from "stylis";

const ExcelFileIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="23" height="23" rx="5.5" fill="#EAF7EF" stroke="#CDEBD9" />
    <rect x="6" y="5.5" width="12" height="13" rx="1.5" fill="#1E9E5A" />
    <rect x="7.5" y="7" width="9" height="10" rx="0.5" fill="#EAF7EF" />
    <rect x="7.5" y="7" width="9" height="2.4" fill="#1E9E5A" />
    <line x1="12" y1="9.4" x2="12" y2="17" stroke="#1E9E5A" strokeWidth="0.9" />
    <line x1="7.5" y1="12.2" x2="16.5" y2="12.2" stroke="#1E9E5A" strokeWidth="0.9" />
    <line x1="7.5" y1="14.6" x2="16.5" y2="14.6" stroke="#1E9E5A" strokeWidth="0.9" />
  </svg>
);

dayjs.extend(utc);

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  handleDownloadExcel,
  onClearFilters
}) {
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const isMediumScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // console.log(table,"tables",canSearch,"cansearch", pagination,"pagination", isSorted,"isSorted",noEndBorder,);
  // Set default value to 50 for entries per page
  const defaultValue = 50;
  const entries = ["5", "8", "15", "20", "25", "50"]; // Add "50" to the entries array
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  const [open, setOpen] = useState(false);
  const [sortOption, setSortOption] = useState('default');
  function customGlobalFilter(rows, columnIds, filterValue) {
    console.log(filterValue, "filterValue");

    // return rows.filter((row) => {
    //   const publication = row.original.publicationText?.toLowerCase() || "";
    //   const packageName = row.original.packageName?.toLowerCase() || "";
    //   console.log(publication, "publication");

    //   return publication.includes(filterValue.toLowerCase());
    // });
    return rows.filter((row) => {
      const search = filterValue?.toLowerCase() || "";

      const publication = row.original.publicationText?.toLowerCase() || "";
      const packageName = row.original.packageName?.toLowerCase() || "";

      return (
        publication.includes(search) ||
        packageName.includes(search)
      );
    });

  }

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const parseDate = (dateString) => {
    if (!dateString || dateString === '-') return null;

    // Try multiple date formats
    const formats = [
      'YYYY-MM-DDTHH:mm:ss.SSSZ',
      'YYYY-MM-DDTHH:mm:ssZ',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD',
      'MM/DD/YYYY HH:mm:ss',
      'MM/DD/YYYY'
    ];

    for (const format of formats) {
      const parsed = dayjs(dateString, format, true);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }

    // Fallback to native Date parsing
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    return null;
  };

  // Updated sort options to match your exact values
  const sortOptions = [
    { value: 'default', label: 'Default' },
    { value: 'price_asc', label: 'Price Asc' },
    { value: 'price_desc', label: 'Price Desc' },
    { value: 'newly_added', label: 'Newly Added' },
    { value: 'recently_modified', label: 'Recently Modified' }
  ];

  // Updated custom sort function - EXACTLY matching your logic
  const applyCustomSort = (data) => {
    return [...data].sort((a, b) => {
      switch (sortOption) {
        case "price_asc":
          return (parseFloat(a.gstPrice) || 0) - (parseFloat(b.gstPrice) || 0);
        case "price_desc":
          return (parseFloat(b.gstPrice) || 0) - (parseFloat(a.gstPrice) || 0);
        case "newly_added":
          const parsedACreated = parseDate(a.createdDate);
          const parsedBCreated = parseDate(b.createdDate);
          if (!parsedACreated && !parsedBCreated) return 0;
          if (!parsedACreated) return 1;
          if (!parsedBCreated) return -1;
          return parsedBCreated - parsedACreated;
        case "recently_modified":
          const parsedAModified = parseDate(a.lastmodifiedDate || a.createdDate);
          const parsedBModified = parseDate(b.lastmodifiedDate || b.createdDate);
          if (!parsedAModified && !parsedBModified) return 0;
          if (!parsedAModified) return 1;
          if (!parsedBModified) return -1;
          return parsedBModified - parsedAModified;
        case "default":
        // default:
        //   if (a.modeType === "INDIA" && b.modeType !== "INDIA") return -1;
        //   if (a.modeType !== "INDIA" && b.modeType === "INDIA") return 1;
        //   return (a.publicationText || "").localeCompare(b.publicationText || "");
        default:
          // 1. Primary Sort: INDIA first
          if (a.modeType === "INDIA" && b.modeType !== "INDIA") return -1;
          if (a.modeType !== "INDIA" && b.modeType === "INDIA") return 1;

          // 2. Secondary Sort: Time level accuracy (Newest First)
          // dayjs(date).valueOf() humein milliseconds deta hai (e.g., 1709123456000)
          const timeA = a.lastmodifiedDate !== "-" ? dayjs(a.lastmodifiedDate).valueOf() : 0;
          const timeB = b.lastmodifiedDate !== "-" ? dayjs(b.lastmodifiedDate).valueOf() : 0;

          return timeB - timeA; // Higher value (newest) upar aayega
      }
    });
  };

  // Apply sorting to data
  const sortedData = useMemo(() => {
    return applyCustomSort([...data]);
  }, [data, sortOption]);

  // const tableInstance = useTable(
  //   {
  //     columns,
  //     data,
  //     globalFilter: customGlobalFilter,
  //     initialState: { pageIndex: 0, pageSize: defaultValue },
  //   },
  //   useGlobalFilter,
  //   useSortBy,
  //   usePagination
  // );

  const tableInstance = useTable(
    {
      columns,
      data: sortedData, // Use sorted data
      globalFilter: customGlobalFilter,
      initialState: { pageIndex: 0, pageSize: defaultValue },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue), [defaultValue]);
  const [controller, dispatch] = useMaterialUIController();
  const { selection } = controller;
  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  useEffect(() => {
    if (onClearFilters) {
      setSortOption('default');
    }
  }, [onClearFilters]);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }
  //   const handleChange = (event) => {
  //   const value = event.target.value;

  //   if (value === "publication") {
  //     localStorage.setItem("publication", "true");
  //   } else if (value === "package") {
  //     localStorage.setItem("publication", "false");
  //   }

  //   console.log("Selected:", value);
  // };

  const handleChange = (event) => {
    setSelection(dispatch, event.target.value);
  };

  const location = useLocation();
  const currentPath = location.pathname;

  const isUserList = currentPath === "/authentication/UserList";

// Floating scrollbar ke liye container styles
const tableWrapperStyles = isUserList ? {
  height: { xs: "60vh", lg: "60vh" }, // Height kam ki gayi
  display: "flex",
  flexDirection: "column",
  overflow: "hidden", // Outer container ko hide rakhein taki inner scroll kare
} : {
  height: { xs: "85vh", sm: "85vh", md: "80vh", lg: "72vh" },
  display: "flex",
  flexDirection: "column",
};

  return (
    <>
      <TableContainer style={{marginBottom:'10px'}}>
        {entriesPerPage || canSearch ? (
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={0.8}
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 1000,
              backgroundColor: "#fff",
            }}
          >
            {currentPath !== "/authentication/UserList" && currentPath !== "/authentication/SignUpUserList" && currentPath !== "/Tables" && (
              <MDBox ml={1}>
                <ToggleGroup.Root
                  type="single"
                  value={selection}
                  onValueChange={(val) => val && setSelection(dispatch, val)}
                  style={{
                    position: "relative",
                    display: "inline-flex",
                    backgroundColor: "transparent",
                    border: "none",
                    borderBottom: "1px solid #e0e0e0",
                    borderRadius: 0,
                    padding: 0,
                    height: isSmallScreen ? 32 : 40,
                    overflow: "hidden",
                    gap: isSmallScreen ? 16 : 28,
                  }}
                >
                  {/* 📘 Publication */}
                  <ToggleGroup.Item
                    value="publication"
                    style={{
                      all: "unset",
                      zIndex: 1,
                      cursor: "pointer",
                      padding: isSmallScreen ? "0 4px 10px" : "0 4px 12px",
                      fontSize: isSmallScreen ? "0.75rem" : "0.9rem",
                      fontWeight: selection === "publication" ? 700 : 500,
                      color: selection === "publication" ? "#1a1a1a" : "#8a8a8a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderBottom: selection === "publication" ? "2px solid #1A83FF" : "2px solid transparent",
                      marginBottom: "-1px",
                      transition: "color 0.2s ease, border-color 0.2s ease",
                    }}
                  >
                    {isSmallScreen ? "Pub" : "Publication"}
                  </ToggleGroup.Item>

                  {/* 📚 Package */}
                  <ToggleGroup.Item
                    value="package"
                    style={{
                      all: "unset",
                      zIndex: 1,
                      cursor: "pointer",
                      padding: isSmallScreen ? "0 4px 10px" : "0 4px 12px",
                      fontSize: isSmallScreen ? "0.75rem" : "0.9rem",
                      fontWeight: selection === "package" ? 700 : 500,
                      color: selection === "package" ? "#1a1a1a" : "#8a8a8a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderBottom: selection === "package" ? "2px solid #1A83FF" : "2px solid transparent",
                      marginBottom: "-1px",
                      transition: "color 0.2s ease, border-color 0.2s ease",
                    }}
                  >
                    {isSmallScreen ? "SM" : "Social Media"}
                  </ToggleGroup.Item>
                </ToggleGroup.Root>
              </MDBox>

            )}

            {currentPath === "/Tables" && (
              <FormControl size="small" sx={{
                minWidth: isSmallScreen ? 70 : 140,  // Smaller width on mobile
                mr: 0.5,
                ml: 0.5,
                '& .MuiSelect-select': {
                  py: isSmallScreen ? 0.8 : 1.2,  // Less padding on mobile
                  fontSize: isSmallScreen ? '0.75rem' : '0.875rem',  // Smaller font
                },
                '& .MuiInputLabel-root': {
                  fontSize: isSmallScreen ? '0.7rem' : '0.75rem',  // Even smaller label
                  lineHeight: 1.2,
                },
                '& .MuiOutlinedInput-root': {
                  minHeight: isSmallScreen ? '36px' : '40px',  // Compact on mobile
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#49a3f1',  // Consistent blue border
                },
                // Mobile-specific adjustments
                ...(isSmallScreen && {
                  '& .MuiSvgIcon-root': {
                    fontSize: '1rem',  // Smaller dropdown arrow
                  }
                })
              }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOption}
                  label="Sort By"
                  onChange={(e) => setSortOption(e.target.value)}
                  endAdornment={<ArrowDropDownIcon />}
                >
                  {sortOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        fontSize: isSmallScreen ? '0.75rem' : '0.875rem',  // Menu items also responsive
                        py: isSmallScreen ? 0.5 : 0.75
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}


            {currentPath !== "/authentication/UserList" && currentPath !== "/authentication/SignUpUserList" && (
              <Tooltip title="Download Excel">
                <Button
                  onClick={handleDownloadExcel}
                  startIcon={<ExcelFileIcon size={18} />}
                  sx={{
                    backgroundColor: "white",
                    color: "#333",
                    marginRight: '10px',
                    border: "1px solid #d7dce1",
                    borderRadius: "6px",
                    padding: isSmallScreen ? "4px 8px" : "6px 14px", // Reduce padding for small screens
                    fontSize: isSmallScreen ? "0.75rem" : "0.875rem", // Reduce font size for small screens
                    fontWeight: 600,
                    minWidth: isSmallScreen ? "50px" : "auto",
                    ml: 1, // Margin-left for spacing
                    textTransform: "none", // Prevent uppercase text
                    "&:hover": {
                      backgroundColor: "#f5f5f5", // Optional: slight hover effect
                      border: "1px solid #d7dce1",
                    },
                  }}
                >
                  {isSmallScreen ? '' : 'Download Excel'}
                </Button>
              </Tooltip>
            )}

            {canSearch && (
              <MDBox
                width="12rem"
                ml="auto"
                sx={{
                  width: {
                    xs: "100%",   // 📱 Full width on mobile
                    sm: "60%",    // 🧩 60% width on small tablets
                    md: "40%",    // 💻 40% on medium screens
                    lg: "30%",    // 🖥️ 30% on large screens
                    xl: "40%",    // 🧭 25% on extra-large screens
                  },
                  position: "relative",
                  right: {
                    // xl:'15%',
                    lg: "5rem",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#49a3f1", // Blue border
                    },
                    "&:hover fieldset": {
                      borderColor: "#49a3f1", // Hover blue
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#49a3f1", // Focus blue
                    },
                  },
                }}
              >
                <MDInput
                  placeholder={currentPath === "/Tables" ? "Search publications..." : "Search..."}
                  value={search}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Icon fontSize="small" sx={{ color: "#9aa0a6", mr: 0.5 }}>
                        search
                      </Icon>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                    },
                  }}
                  onChange={({ currentTarget }) => {
                    setSearch(search);
                    onSearchChange(currentTarget.value);
                  }}
                />

              </MDBox>
            )}
          </MDBox>
        ) : null}
      </TableContainer>

      <MDBox sx={tableWrapperStyles}>
        {" "}
        {/* 🔄 CHANGED from outer TableContainer */}
        {/* <TableContainer sx={{ boxShadow: "none", height:"60vh",maxHeight:"100vh"}}> */}
        {/* // Wrap table in scrollable container */}
        <TableContainer
          sx={{
            flexGrow: 1, // 🆕 NEW: allow it to grow and fill space
            overflowY: "auto", // 🔄 CHANGED from fixed height
            overflowX: "auto", // 🔄 CHANGED from fixed height
            boxShadow: "none",
            "&::-webkit-scrollbar": {
              height: "6px",
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#CECECE",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#DFDEDE",
            },
          }}
        >
          <Table {...getTableProps()} stickyHeader>
            <thead>
              {headerGroups.map((headerGroup, key) => (
                <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, idx) => (
                    <DataTableHeadCell
                      key={idx}
                      header={column.Header}
                      {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                      width={column.width ? column.width : "auto"}
                      align={column.align ? column.align : "left"}
                      sorted={setSortedValue(column)}
                    >
                      {column.render("Header")}
                    </DataTableHeadCell>
                  ))}
                </TableRow>
              ))}
            </thead>

            <TableBody {...getTableBodyProps()}>
              {page.map((row, key) => {
                prepareRow(row);
                return (
                  <TableRow key={key} {...row.getRowProps()}>
                    {row.cells.map((cell, idx) => (

                      <DataTableBodyCell
                        key={idx}
                        noBorder={noEndBorder && rows.length - 1 === key}
                        align={cell.column.align ? cell.column.align : "left"}
                        isPublicationColumn={cell.column.Header === "Publication"}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </DataTableBodyCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {/* </TableContainer> */}
        {/* <TableContainer sx={{position:"sticky", bottom:0, top:0}}> */}
        <MDBox
          sx={{
            backgroundColor: "#fff", // 🆕 NEW: background for separation
            borderTop: "1px solid #e0e0e0", // 🆕 NEW: top border
            zIndex: 1000, // 🆕 NEW: ensure it stays on top
          }}
          display="flex"
          flexDirection={{ xs: "row", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={!showTotalEntries && pageOptions.length === 1 ? 0 : 2}
        >
          {showTotalEntries && (
            <MDBox mb={{ xs: 0, sm: 0 }} >
              <MDTypography variant="button" color="secondary" fontWeight="regular">
                Showing {entriesStart} to {entriesEnd} of {rows.length} entries
              </MDTypography>
            </MDBox>
          )}
          {pageOptions.length > 1 && (
            <MDPagination
              variant={pagination.variant ? pagination.variant : "gradient"}
              color={pagination.color ? pagination.color : "info"} // Default color
            >
              {entriesPerPage && isLargeScreen && (
                <MDBox display="flex" alignItems="center">
                  <MDTypography variant="caption" color="secondary">
                    &nbsp;Entries&nbsp;&nbsp;&nbsp;
                  </MDTypography>
                  <Autocomplete
                    disableClearable
                    value={pageSize.toString()}
                    options={entries}
                    onChange={(event, newValue) => {
                      setEntriesPerPage(parseInt(newValue, 10));
                    }}
                    size="small"
                    sx={{
                      width: "4rem",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#49a3f1", // Blue border
                        },
                        "&:hover fieldset": {
                          borderColor: "#49a3f1", // Hover blue
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#49a3f1", // Focus blue
                        },
                      },
                    }}
                    renderInput={(params) => <MDInput {...params} />}
                  />
                </MDBox>
              )}
              {canPreviousPage && (
                <MDPagination
                  item
                  onClick={() => previousPage()}
                  sx={{
                    backgroundColor: "#1965e4", // Default background color
                    color: "white", // Default text color
                    "&:hover": {
                      backgroundColor: "#1965e4", // Keep the same color on hover
                      color: "white", // Keep the same text color on hover
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#1965e4", // Keep the same color when active
                      color: "white", // Keep the same text color when active
                    },
                  }}
                >
                  <Icon sx={{ fontWeight: "bold", color: "#fff !important" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination.length > 6 ? (
                <MDBox width="5rem" mx={1}>
                  <MDInput
                    inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                    value={customizedPageOptions[pageIndex]}
                    onChange={(handleInputPagination, handleInputPaginationValue)}
                  />
                </MDBox>
              ) : (
                renderPagination
              )}
              {canNextPage && (
                <MDPagination
                  item
                  onClick={() => nextPage()}
                  disableRipple   // ✅ VERY IMPORTANT
                  sx={{
                    backgroundColor: "#1965e4 !important",
                    color: "#fff !important",

                    "&:hover": {
                      backgroundColor: "#1965e4 !important",
                      color: "#fff !important",
                    },

                    "&:focus": {
                      backgroundColor: "#1965e4 !important",
                      color: "#fff !important",
                    },

                    "&:focus-visible": {
                      backgroundColor: "#1965e4 !important",
                      color: "#fff !important",
                    },

                    "&.Mui-selected": {
                      backgroundColor: "#1965e4 !important",
                      color: "#fff !important",
                    },

                    "&.MuiPaginationItem-root": {
                      backgroundColor: "#1965e4 !important",
                    },
                  }}
                >
                  <Icon sx={{ fontWeight: "bold", color: "#fff !important" }}>
                    chevron_right
                  </Icon>
                </MDPagination>
              )}

            </MDPagination>
          )}
        </MDBox>
        {/* </TableContainer> */}
      </MDBox>
    </>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 50, entries: [5, 8, 15, 20, 25, 50] }, // Set default to 50
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  onClearFilters: PropTypes.number,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  header: PropTypes.string,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  handleDownloadExcel: PropTypes.func,
};

export default DataTable;